import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { motion, useReducedMotion } from "framer-motion"
import { toast } from "sonner"
import {
  ArrowLeft, Target, Dumbbell, Timer, MessageCircle,
  Plus, X, Check,
} from "lucide-react"
import { useMonitoring } from "@/hooks/useMonitoring"
import type { ProgrammeTrend } from "@/lib/monitoring/calculations"
import { useProfile } from "@/hooks/useProfile"
import { addWeightEntry, getWeightHistory, type WeightHistoryRow } from "@/lib/supabase"
import { parseLocalDate, toLocalISODate, formatFr } from "@/lib/utils"
import { BottomNav } from "@/components/BottomNav"
import { ClockGauge } from "@/components/monitoring/ClockGauge"
import { NumberWheel } from "@/components/shared/NumberWheel"

interface ProgressPageProps {
  email: string
  firstName?: string
  goalId?: string
}

// Pesée du jour : molette du diagnostic, mêmes bornes (40–200 kg), pas de 0,5 kg.
const WEIGH_IN = { min: 40, max: 200, step: 0.5, fallback: 75 }

// ── Palette maquette ────────────────────────────────────────────────
const C = {
  card: "#0b1220",
  line: "rgba(255,255,255,0.07)",
  cyan: "#06b6d4",
  cyan2: "#22d3ee",
  txt2: "#c3ccd9",
  txt3: "#8b98ad",
  txt4: "#7d8ba1",
  txt5: "#64748b",
  txt6: "#5f6d82",
  yLabel: "#5b6a80",
}

// Sémantique (quel indicateur est vert/orange/rouge) reprise du bilan hebdo
// (computeCurrentStatus) ; nuances 400 pour le texte et les icônes, comme la maquette.
const ALERT_GREEN = "#4ade80"
const ALERT_ORANGE = "#fb923c"
const ALERT_RED = "#f87171"
const ALERT_GREY = "#94a3b8"

const MONTHS_FR = [
  "janv.", "févr.", "mars", "avr.", "mai", "juin",
  "juill.", "août", "sept.", "oct.", "nov.", "déc.",
]

function signedKg(n: number): string {
  const sign = n > 0 ? "+" : n < 0 ? "−" : ""
  return `${sign}${formatFr(Math.abs(n))} kg`
}

function shortDate(d: Date): string {
  return `${d.getDate()} ${MONTHS_FR[d.getMonth()]}`
}

// ── Règle de couleur des deltas : dépend de l'objectif, pas du signe ─
type GoalDir = "gain" | "loss" | "maintain"

// Mapping objectif → direction de poids attendue, câblé pour TOUS les objectifs
// du wizard (cf. src/data/labels.ts GOAL_LABELS), y compris construction et découpe.
const GOAL_DIRECTION: Record<string, GoalDir> = {
  "perte-de-poids": "loss",
  "prise-de-muscle": "gain",
  "remise-en-forme": "maintain",
  "seche-definition": "loss",
  "construction-musculaire": "gain",
  "force-endurance": "maintain",
  "decoupe-extreme": "loss",
  "hypertrophie": "gain",
  "force-athletique": "maintain",
}

type ToneKey = "vert" | "rouge" | "orange" | "gris"

const TONE: Record<ToneKey, { fg: string; lb: string; bg: string; bd: string }> = {
  vert: { fg: "#4ade80", lb: "#8fce9f", bg: "rgba(34,197,94,0.11)", bd: "rgba(34,197,94,0.28)" },
  rouge: { fg: "#f87171", lb: "#d29a9a", bg: "rgba(239,68,68,0.11)", bd: "rgba(239,68,68,0.30)" },
  orange: { fg: "#fb923c", lb: "#c9a183", bg: "rgba(251,146,60,0.11)", bd: "rgba(251,146,60,0.28)" },
  gris: { fg: "#94a3b8", lb: "#7d8ba1", bg: "rgba(148,163,184,0.09)", bd: "rgba(148,163,184,0.22)" },
}

// Seuil 200 g = bruit de balance (hydratation, heure de pesée) : en dessous, aucune couleur sémantique.
function weightTone(deltaKg: number, dir: GoalDir): ToneKey {
  if (dir === "maintain") return Math.abs(deltaKg) <= 1 ? "gris" : "orange"
  if (Math.abs(deltaKg) < 0.2) return "gris"
  const goodWhenUp = dir === "gain"
  return (goodWhenUp ? deltaKg > 0 : deltaKg < 0) ? "vert" : "rouge"
}

// Anneau de score + libellé sous l'anneau : source unique pour le texte ET la
// couleur (seuils 80 / 50 / 25). La couleur alimente à la fois le libellé et la
// prop `color` de ClockGauge → l'anneau et le texte sont toujours accordés.
function scoreTone(score: number): { label: string; color: string } {
  if (score >= 80) return { label: "Excellent", color: C.cyan2 }   // #22d3ee
  if (score >= 50) return { label: "Bon", color: TONE.vert.fg }    // #4ade80
  if (score >= 25) return { label: "Moyen", color: TONE.orange.fg } // #fb923c
  return { label: "À améliorer", color: TONE.rouge.fg }            // #f87171
}

// ── Bucketisation hebdo (série CONTINUE) ────────────────────────────
const WEEK_MS = 604800000

// Lundi (00:00 local) de la semaine ISO de `d`.
function isoWeekMonday(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const dow = (x.getDay() + 6) % 7 // 0 = lundi
  x.setDate(x.getDate() - dow)
  return x
}

interface WeekPoint {
  w: number // S1, S2… — ancré à la 1re pesée de l'historique, stable entre fenêtres
  kg: number | null // null = aucune pesée cette semaine (le créneau reste dans la série)
  date: Date | null // date réelle de la pesée (affichée au tap)
  label: string | null
}

// Série hebdomadaire CONTINUE, de la 1re à la dernière semaine pesée. Les semaines
// sans pesée gardent leur créneau (kg = null, comme les buckets Hooper S24-2) : deux
// pesées à un mois d'écart restent à un mois d'écart sur l'axe — pente et régression
// non faussées. 1 point pesé = dernière pesée de la semaine (lignes triées par date
// croissante, le dernier `set` sur une clé gagne) → le dernier point vaut le
// « Poids actuel » affiché en tête.
function toWeekPoints(rows: WeightHistoryRow[]): WeekPoint[] {
  if (rows.length === 0) return []
  const mondayMs = (iso: string) => isoWeekMonday(parseLocalDate(iso)).getTime()
  const first = Math.min(...rows.map((r) => mondayMs(r.date)))
  const byIdx = new Map<number, WeightHistoryRow>()
  for (const r of rows) byIdx.set(Math.round((mondayMs(r.date) - first) / WEEK_MS), r)

  const total = Math.max(...byIdx.keys()) + 1
  const out: WeekPoint[] = []
  for (let i = 0; i < total; i++) {
    const r = byIdx.get(i)
    if (!r) {
      out.push({ w: i + 1, kg: null, date: null, label: null })
      continue
    }
    const d = parseLocalDate(r.date)
    out.push({ w: i + 1, kg: r.weight, date: d, label: shortDate(d) })
  }
  return out
}

export const ProgressPage = ({ goalId }: ProgressPageProps) => {
  const navigate = useNavigate()
  const { profile } = useProfile()
  const {
    progress, wellness, loads, intensity, volume, loading,
  } = useMonitoring(30)

  const [weightHistory, setWeightHistory] = useState<WeightHistoryRow[]>([])
  const [weightLoading, setWeightLoading] = useState(true)
  const [showAddWeight, setShowAddWeight] = useState(false)
  const [newWeight, setNewWeight] = useState<number>(WEIGH_IN.fallback)
  const [savingWeight, setSavingWeight] = useState(false)

  useEffect(() => {
    getWeightHistory(365)
      .then(setWeightHistory)
      .catch(console.error)
      .finally(() => setWeightLoading(false))
  }, [])

  // Un seul écran de chargement (motif SessionsHistoryPage) couvre TOUT le fetch
  // initial — pas de rendu partiel du score / des tuiles / du graphe.
  const pageLoading = loading || weightLoading

  // La molette s'ouvre sur la dernière pesée enregistrée, telle quelle (72,4 reste
  // 72,4 si on enregistre sans toucher) ; sans pesée, sur le poids du profil. Fixée
  // avant l'ouverture : la molette ne lit sa position de départ qu'au montage.
  const openAddWeight = () => {
    const candidates = [weightHistory[weightHistory.length - 1]?.weight, profile?.weight]
    const start = candidates.find((w): w is number => w != null && Number.isFinite(w) && w > 0)
    setNewWeight(start ?? WEIGH_IN.fallback)
    setShowAddWeight(true)
  }

  const handleAddWeight = async () => {
    setSavingWeight(true)
    try {
      const today = toLocalISODate(new Date())
      const newEntry = await addWeightEntry(today, newWeight)
      setWeightHistory((prev) => {
        const filtered = prev.filter((e) => e.date !== today)
        return [...filtered, newEntry].sort((a, b) => a.date.localeCompare(b.date))
      })
      toast.success("Poids enregistré")
      setShowAddWeight(false)
    } catch (err) {
      toast.error("Erreur", { description: err instanceof Error ? err.message : "Sauvegarde échouée" })
    } finally {
      setSavingWeight(false)
    }
  }

  const weekPoints = useMemo(() => toWeekPoints(weightHistory), [weightHistory])

  // ── Poids : valeurs affichées ────────────────────────────────────
  // Dernier créneau de la série continue = toujours une semaine pesée (par construction).
  const lastPoint = weekPoints[weekPoints.length - 1]
  const prevPoint = weekPoints[weekPoints.length - 2] // peut être une semaine vide (kg = null)
  const currentWeight =
    lastPoint?.kg ?? weightHistory[weightHistory.length - 1]?.weight ?? profile?.weight ?? 0
  const lastWeighIn = weightHistory[weightHistory.length - 1]?.date
  const startWeight = profile?.weightStart ?? currentWeight

  const dir: GoalDir = GOAL_DIRECTION[goalId ?? profile?.raw.goal_id ?? ""] ?? "maintain"

  // « vs sem. préc. » invalidé si la semaine N−1 ne porte aucune pesée — sinon
  // le libellé mentirait en comparant avec une pesée plus ancienne.
  const deltaWeek =
    prevPoint && prevPoint.kg !== null ? currentWeight - prevPoint.kg : null
  const deltaStart = currentWeight - startWeight
  const toneWeek: ToneKey | null = deltaWeek === null ? null : weightTone(deltaWeek, dir)
  const toneStart: ToneKey = weightTone(deltaStart, dir)

  // ── Alerte contextuelle : score global + garde-fou d'incohérence ──
  const coach = getCoachAlert({
    loading,
    hasProfile: !!profile,
    hasWellness: wellness.length > 0,
    hasLoads: loads.length > 0,
    score: progress.score,
    // Garde-fou câblé sur la tendance longue (« depuis le début ») uniquement :
    // le delta hebdomadaire est trop bruité pour piloter le ton de l'alerte.
    startDeltaRed: toneStart === "rouge",
  })

  const scoreVis = scoreTone(progress.score)

  return (
    <div className="min-h-screen bg-[#060a13] cursor-default pb-24 relative">
      <div className="fixed inset-0 z-0 pointer-events-none">
        <img src="/dashboard-bg.png" alt="" className="w-full h-full object-cover opacity-[0.05]" />
      </div>

      {/* HEADER */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="px-5 pt-5 pb-4 relative z-10">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/dashboard")}
            className="w-[34px] h-[34px] rounded-[11px] flex items-center justify-center hover:bg-white/[0.09] cursor-pointer"
            style={{ background: "rgba(255,255,255,0.05)" }}
          >
            <ArrowLeft className="w-[17px] h-[17px]" style={{ color: C.txt2 }} />
          </button>
          <div>
            <h1 className="text-[19px] font-medium text-white tracking-[-0.2px]">Progrès</h1>
            <p className="text-[11px] mt-0.5" style={{ color: C.txt4 }}>Score, performance, régularité</p>
          </div>
        </div>
      </motion.div>

      {pageLoading ? (
        <div className="px-4 mt-6 flex flex-col items-center gap-2 relative z-10">
          <div className="w-6 h-6 rounded-full border-2 border-gray-700 border-t-cyan-400 animate-spin" />
          <p className="text-[12px] text-gray-500">Chargement…</p>
        </div>
      ) : (
      <>
      {/* SCORE GLOBAL — anneau ClockGauge existant */}
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.05 }} className="px-4 relative z-10">
        <div
          className="rounded-2xl text-center"
          style={{ background: C.card, border: `0.5px solid ${C.line}`, padding: "18px 14px 14px" }}
        >
          {/* En-tête neutre comme les autres sections — ne suit PAS la couleur dynamique du score */}
          <div className="text-[10.5px] uppercase tracking-[1px] mb-2.5" style={{ color: C.txt6 }}>Score global</div>
          <div className="flex justify-center">
            <ClockGauge
              value={progress.score}
              max={100}
              color={scoreVis.color}
              size={132}
              valueSize={34}
              denom="/ 100"
              delay={0.15}
            />
          </div>
          <div className="text-[13px] font-medium mt-0.5" style={{ color: scoreVis.color }}>{scoreVis.label}</div>
        </div>
      </motion.div>

      {/* ALERTE CONTEXTUELLE */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="px-4 mt-2.5 relative z-10">
        <div
          className="rounded-[14px]"
          style={{ background: `${coach.color}14`, border: `0.5px solid ${coach.color}38`, padding: "12px 13px" }}
        >
          <div className="flex items-center gap-[7px] mb-[5px]">
            <MessageCircle className="w-[15px] h-[15px]" style={{ color: coach.color }} />
            <span className="text-[12.5px] font-medium" style={{ color: coach.color }}>{coach.title}</span>
          </div>
          <div className="text-[12px] leading-[1.55]" style={{ color: "#a8b4c4" }}>{coach.text}</div>
        </div>
      </motion.div>

      {/* PERFORMANCE DÉCOMPOSÉE — 3 tuiles */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="px-4 mt-[18px] relative z-10">
        <SectionLabel>Performance décomposée</SectionLabel>
        <div className="grid grid-cols-3 gap-2">
          <Tile
            icon={Target}
            tone={TONE.vert.fg}
            value={`${progress.regularity} %`}
            label="Régularité"
            sub={`${wellness.length} / 30 jours`}
          />
          <Tile icon={Dumbbell} label="Intensité" {...trendTile(intensity, TONE.orange.fg)} />
          <Tile icon={Timer} label="Volume" {...trendTile(volume, C.cyan2)} />
        </div>

        {/* Explication grand public des deux indicateurs */}
        <div className="rounded-xl mt-2" style={{ background: "rgba(255,255,255,0.025)", padding: "11px 12px" }}>
          <div className="flex gap-[9px] mb-[9px]">
            <span className="mt-[6px] shrink-0" style={{ width: 5, height: 5, borderRadius: 99, background: TONE.orange.fg }} />
            <div className="text-[11.5px] leading-[1.5]" style={{ color: C.txt3 }}>
              <span className="font-medium" style={{ color: C.txt2 }}>Intensité</span> — le poids total que tu as soulevé sur ta dernière semaine de programme terminée, comparé à la précédente.
            </div>
          </div>
          <div className="flex gap-[9px]">
            <span className="mt-[6px] shrink-0" style={{ width: 5, height: 5, borderRadius: 99, background: C.cyan2 }} />
            <div className="text-[11.5px] leading-[1.5]" style={{ color: C.txt3 }}>
              <span className="font-medium" style={{ color: C.txt2 }}>Volume</span> — le temps où tes muscles ont vraiment travaillé, même comparaison.
            </div>
          </div>
          <div className="text-[11px] leading-[1.5] mt-[7px]" style={{ color: C.txt5 }}>
            Les points en haut de chaque tuile suivent la semaine en cours : le chiffre se met à jour quand elle est terminée.
          </div>
        </div>
      </motion.div>

      {/* SUIVI DU POIDS */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="px-4 mt-[18px] relative z-10">
        <SectionLabel
          right={
            <button
              onClick={openAddWeight}
              className="flex items-center gap-[3px] text-[12px] cursor-pointer hover:opacity-80"
              style={{ color: C.cyan2 }}
            >
              <Plus className="w-[13px] h-[13px]" />
              Ajouter
            </button>
          }
        >
          Suivi du poids
        </SectionLabel>

        <div className="rounded-2xl" style={{ background: C.card, border: `0.5px solid ${C.line}`, padding: "16px 14px 12px" }}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-[11px] mb-[5px]" style={{ color: C.txt4 }}>Poids actuel</div>
              <div className="flex items-baseline gap-1">
                <span className="text-[36px] font-medium leading-none tracking-[-0.8px] text-white">{formatFr(currentWeight)}</span>
                <span className="text-[14px]" style={{ color: C.txt4 }}>kg</span>
              </div>
              {lastWeighIn && (
                <div className="text-[10.5px] mt-1.5" style={{ color: C.txt5 }}>
                  Dernière pesée · {shortDate(parseLocalDate(lastWeighIn))}
                </div>
              )}
            </div>
            {weekPoints.length > 0 && (
              <div className="flex flex-col gap-[7px] items-end">
                {deltaWeek !== null && toneWeek && (
                  <DeltaPill tone={toneWeek} value={signedKg(deltaWeek)} label="vs sem. préc." />
                )}
                <DeltaPill tone={toneStart} value={signedKg(deltaStart)} label="depuis le début" />
              </div>
            )}
          </div>

          {weekPoints.length > 1 ? (
            <WeightChart points={weekPoints} />
          ) : (
            <p className="text-[12px] text-center py-4" style={{ color: C.txt5 }}>
              Ajoute tes pesées pour voir l'évolution
            </p>
          )}
        </div>
      </motion.div>
      </>
      )}

      {/* MODAL Add Weight */}
      {showAddWeight && (
        <>
          <div className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-[4px]" onClick={() => !savingWeight && setShowAddWeight(false)} />
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="fixed z-[100] left-4 right-4 top-1/2 -translate-y-1/2 max-w-[400px] mx-auto p-6 rounded-2xl"
            style={{ background: "linear-gradient(180deg,#0f1829,#0a1020)", border: "1px solid rgba(30,41,59,0.55)" }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                {/* Même illustration que la tuile « Poids » du Profil, à la hauteur de la croix. */}
                <img src="/illustrations/poids.svg" alt="" aria-hidden className="w-8 h-8 shrink-0" />
                <h3 className="text-[16px] font-bold text-white">Pesée du jour</h3>
              </div>
              <button onClick={() => !savingWeight && setShowAddWeight(false)} className="w-8 h-8 rounded-lg flex items-center justify-center cursor-pointer hover:bg-white/[0.04]">
                <X className="w-4 h-4 text-gray-400" />
              </button>
            </div>
            <div className="flex justify-center mb-4">
              <NumberWheel
                id="progress-weigh-in"
                label="Poids du jour"
                labelHidden
                unit="kg"
                min={WEIGH_IN.min}
                max={WEIGH_IN.max}
                step={WEIGH_IN.step}
                value={newWeight}
                onChange={setNewWeight}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => setShowAddWeight(false)} disabled={savingWeight}
                className="py-3 rounded-xl text-[14px] font-semibold text-gray-400 cursor-pointer disabled:opacity-50"
                style={{ border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)" }}>
                Annuler
              </button>
              <button onClick={handleAddWeight} disabled={savingWeight}
                className="py-3 rounded-xl text-[14px] font-bold text-white cursor-pointer disabled:opacity-70 flex items-center justify-center gap-2"
                style={{ background: "linear-gradient(135deg,#0369a1,#06b6d4)" }}>
                {savingWeight ? <div className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" /> : <Check className="w-4 h-4" />}
                {savingWeight ? "…" : "Enregistrer"}
              </button>
            </div>
          </motion.div>
        </>
      )}

      <BottomNav />
    </div>
  )
}

// ============================================
// ALERTE CONTEXTUELLE (score global)
// ============================================

// Source = score global (progress.score), pas computeCurrentStatus() : le texte
// reprend l'anneau juste au-dessus, computeCurrentStatus (charge/fatigue) est déjà
// porté par le Monitoring. Seule la SÉMANTIQUE vert/orange/rouge vient du bilan
// hebdo (nuances 400 pour texte + icône). Garde-fou : jamais une alerte verte
// au-dessus d'un delta « depuis le début » rouge — on rétrograde en orange.
function getCoachAlert(x: {
  loading: boolean
  hasProfile: boolean
  hasWellness: boolean
  hasLoads: boolean
  score: number
  startDeltaRed: boolean
}): { title: string; text: string; color: string } {
  if (x.loading || !x.hasProfile) {
    return { title: "Chargement", text: "Récupération de tes données…", color: ALERT_GREY }
  }
  if (!x.hasWellness) {
    return {
      title: "Démarrons ensemble",
      text: "Remplis ton premier questionnaire du matin pour lancer le suivi.",
      color: ALERT_GREY,
    }
  }
  if (!x.hasLoads) {
    return {
      title: "Première séance",
      text: "Enregistre ta première séance via Post-séance pour activer le suivi de charge.",
      color: ALERT_GREY,
    }
  }

  if (x.score >= 50 && x.startDeltaRed) {
    return {
      title: "Régularité OK, poids à surveiller",
      text: "Ta régularité tient, mais depuis le début du suivi ton poids évolue à l'inverse de ton objectif. Regarde ton alimentation avant de pousser l'entraînement.",
      color: ALERT_ORANGE,
    }
  }
  if (x.score >= 80) {
    return {
      title: "Excellente régularité",
      text: "Tu es très constant. Continue ainsi pour ancrer tes habitudes.",
      color: ALERT_GREEN,
    }
  }
  if (x.score >= 50) {
    return {
      title: "Sur la bonne voie",
      text: "Régularité correcte. Vise un peu plus de constance pour booster ta progression.",
      color: ALERT_GREEN,
    }
  }
  return {
    title: "À reprendre",
    text: "Quelques séances ratées récemment. Programme tes prochaines sessions !",
    color: ALERT_RED,
  }
}

// ============================================
// TUILES PERFORMANCE
// ============================================

// Le grand chiffre est le verdict de la DERNIÈRE SEMAINE DE PROGRAMME COMPLÈTE —
// exactement celui qui entre dans le score (cf. trendForScore) — et jamais le %
// partiel de la semaine en cours : mesuré sur le catalogue, ce dernier a le
// mauvais signe dans 34 % des semaines après une séance (15 % après deux). La
// semaine en cours n'apparaît que par ses points d'avancement (Tile `progress`).
//
// Semaine de décharge : jamais de pourcentage choc sur une baisse planifiée —
// message explicatif à la place, même traitement Intensité/Volume.
//
// Reprise : quand la dernière semaine complète est une décharge et que la
// suivante a commencé, afficher « Décharge » pendant toute la reprise serait
// faux (elle est finie). On bascule alors sur la semaine en cours, comparée au
// niveau d'avant décharge — seule exception au « verdict d'abord », sans enjeu
// de signe puisque la tuile n'y porte pas de verdict vert/rouge.
//
// `referenceIsPreDeload` (semaine de reprise complète) : la tuile n'est plus un
// verdict vert/rouge mais un relevé de STATUT — « N % du niveau d'avant la
// décharge », ton neutre. Cohérent avec le score, que trendForScore protège
// d'une baisse de reprise : pas de « deux tuiles rouges sous un score stable ».
// Voir CONTEXTE_V25 §7.
//
// `coverageDegraded` (cf. isCoverageDegraded) : les deux semaines comparées n'ont
// pas été lues avec la même finesse — le pourcentage reste une vraie mesure, mais
// partielle, donc il garde sa couleur (ce n'est pas un non-verdict comme Décharge
// ou « — ») et prend le « ~ » de l'écran exercice, plus un sous-titre qui dit la
// cause : le tilde seul porterait ici un sens différent de là-bas (valeur estimée
// faute de 1RM mesuré). Le score, lui, ignore ce trend — cf. trendForScore.
function trendTile(
  t: ProgrammeTrend,
  baseColor: string
): Pick<TileProps, "value" | "sub" | "tone" | "estimated" | "progress"> {
  const { current, lastComplete } = t
  const progress = current
    ? { done: Math.min(current.sessionsDone, current.sessionsPrescribed), total: current.sessionsPrescribed, tone: baseColor }
    : undefined

  if (lastComplete?.isDeloadWeek && current !== null && current !== lastComplete) {
    if (current.trend === null) {
      return { value: "—", sub: "Reprise post-décharge", tone: C.txt5, progress }
    }
    return {
      value: `${Math.round(100 + current.trend)} %`,
      sub: "Reprise post-décharge",
      tone: C.txt5,
      estimated: current.coverageDegraded,
      progress,
    }
  }

  if (lastComplete === null || (lastComplete.trend === null && !lastComplete.isDeloadWeek)) {
    return { value: "—", sub: "pas de comparaison", tone: C.txt5, progress }
  }
  if (lastComplete.isDeloadWeek) {
    return { value: "Décharge", sub: "Semaine planifiée", tone: C.txt5, progress }
  }
  const trend = lastComplete.trend as number
  if (lastComplete.referenceIsPreDeload) {
    // Le statut de reprise reste plus informatif que « mesure partielle » : on
    // garde son sous-titre et on ne marque que le chiffre.
    return {
      value: `${Math.round(100 + trend)} %`,
      sub: "du niveau d'avant décharge",
      tone: C.txt5,
      estimated: lastComplete.coverageDegraded,
      progress,
    }
  }
  return {
    value: trend > 0 ? `+${trend} %` : `${trend} %`,
    sub: lastComplete.coverageDegraded ? "mesure partielle" : "vs sem. préc.",
    tone: baseColor,
    estimated: lastComplete.coverageDegraded,
    progress,
  }
}

interface TileProps {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>
  tone: string
  value: string
  label: string
  sub: string
  /** Préfixe "~" sur le chiffre — même convention que la tuile Charge de l'écran
   *  exercice : le tilde qualifie le nombre qui SUIT, donc collé, plus petit et en
   *  ton secondaire (cf. renderValue dans session/ExerciseParams.tsx). */
  estimated?: boolean
  /** Avancement de la semaine de programme en cours : un point par séance
   *  prescrite, plein si loggée. Discret par construction — le chiffre reste
   *  l'information principale. `tone` = couleur de l'indicateur, indépendante
   *  du ton du chiffre (neutre en décharge/reprise). */
  progress?: { done: number; total: number; tone: string }
}

function Tile({ icon: Icon, tone, value, label, sub, estimated, progress }: TileProps) {
  return (
    <div className="rounded-[14px]" style={{ background: C.card, border: `0.5px solid ${C.line}`, padding: "11px 10px" }}>
      <div className="flex items-start justify-between mb-[9px]">
        <div
          className="flex items-center justify-center"
          style={{ width: 26, height: 26, borderRadius: 8, background: `${tone}1f` }}
        >
          <Icon className="w-[14px] h-[14px]" style={{ color: tone }} />
        </div>
        {progress && progress.total > 0 && (
          <div
            role="img"
            aria-label={`Semaine en cours : ${progress.done} séance${progress.done > 1 ? "s" : ""} sur ${progress.total}`}
            className="flex gap-[3px] mt-[3px]"
          >
            {Array.from({ length: progress.total }, (_, i) => (
              <span
                key={i}
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: 99,
                  background: i < progress.done ? progress.tone : "rgba(255,255,255,0.14)",
                }}
              />
            ))}
          </div>
        )}
      </div>
      <div className="text-[19px] font-medium leading-[1.1]" style={{ color: tone }}>
        {estimated && <span className="text-[13px] font-semibold" style={{ color: C.txt3 }}>~</span>}
        {value}
      </div>
      <div className="text-[10.5px] mt-[3px]" style={{ color: C.txt2 }}>{label}</div>
      <div className="text-[9.5px]" style={{ color: C.txt5 }}>{sub}</div>
    </div>
  )
}

function SectionLabel({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between mx-1 mb-2">
      <span className="text-[10.5px] tracking-[1px] uppercase" style={{ color: C.txt6 }}>{children}</span>
      {right}
    </div>
  )
}

function DeltaPill({ tone, value, label }: { tone: ToneKey; value: string; label: string }) {
  const t = TONE[tone]
  return (
    <span
      className="inline-flex items-baseline gap-1.5 rounded-full"
      style={{ padding: "6px 11px", background: t.bg, border: `0.5px solid ${t.bd}` }}
    >
      <span className="text-[13px] font-medium" style={{ color: t.fg }}>{value}</span>
      <span className="text-[9.5px]" style={{ color: t.lb }}>{label}</span>
    </span>
  )
}

// ============================================
// GRAPHE DE POIDS
// ============================================

const H = 176
const PT = 14
const PB = 26
const PL = 10
const PR = 12
const PLOT_H = H - PT - PB
const AX_W = 30
const GAP = 8

type WindowN = 6 | 12 | 0
const WINDOWS: { n: WindowN; label: string }[] = [
  { n: 6, label: "6 sem" },
  { n: 12, label: "12 sem" },
  { n: 0, label: "Tout" },
]

function catmullRom(p: { x: number; y: number }[]): string {
  let d = `M${p[0].x},${p[0].y}`
  for (let i = 0; i < p.length - 1; i++) {
    const p0 = p[i - 1] || p[i]
    const p1 = p[i]
    const p2 = p[i + 1]
    const p3 = p[i + 2] || p2
    d += ` C${p1.x + (p2.x - p0.x) / 6},${p1.y + (p2.y - p0.y) / 6} ${p2.x - (p3.x - p1.x) / 6},${p2.y - (p3.y - p1.y) / 6} ${p2.x},${p2.y}`
  }
  return d
}

function WeightChart({ points }: { points: WeekPoint[] }) {
  const reduce = useReducedMotion()
  const scrollRef = useRef<HTMLDivElement>(null)
  const [avail, setAvail] = useState(290)
  const [windowN, setWindowN] = useState<WindowN>(12)
  const [active, setActive] = useState<number | null>(null)
  const [drawn, setDrawn] = useState(false)

  // Largeur réelle de la zone de tracé
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const measure = () => setAvail(el.clientWidth || 290)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Animation déclenchée une seule fois à l'apparition
  useEffect(() => {
    if (reduce) { setDrawn(true); return }
    const id = requestAnimationFrame(() => setDrawn(true))
    return () => cancelAnimationFrame(id)
  }, [reduce])

  const geo = useMemo(() => {
    const rows = windowN ? points.slice(-windowN) : points
    const n = rows.length // créneaux de semaine, semaines vides incluses
    const inner = windowN
      ? avail - PL - PR
      : Math.max(avail - PL - PR, (n - 1) * 36) // pas mini 36 px/sem en « Tout »
    const W = inner + PL + PR

    // Créneaux réellement pesés (les semaines vides gardent leur position en x
    // mais ne portent ni point, ni segment de courbe, ni terme de régression).
    const filled = rows
      .map((r, i) => ({ r, i }))
      .filter((e) => e.r.kg !== null)
    const vals = filled.map((e) => e.r.kg as number)

    let lo = Math.min(...vals) - 0.4
    let hi = Math.max(...vals) + 0.4
    // Amplitude minimale 4 kg (sinon une semaine stable donne un faux yo-yo)
    if (hi - lo < 4) {
      const c = (hi + lo) / 2
      lo = c - 2
      hi = c + 2
    }
    lo = Math.floor(lo)
    hi = Math.ceil(hi)
    while ((hi - lo) % 3 !== 0) hi++ // écart multiple de 3 → 4 graduations entières

    const y = (v: number) => PT + PLOT_H - ((v - lo) / (hi - lo)) * PLOT_H
    const x = (i: number) => PL + (n === 1 ? inner / 2 : (i * inner) / (n - 1))
    const pts = filled.map((e) => ({ x: x(e.i), y: y(e.r.kg as number), r: e.r, i: e.i }))

    // Tendance : régression linéaire sur les points réels, variable x = index de
    // semaine dans la fenêtre (série continue → un trou de 3 semaines pèse +4, pas +1).
    let sx = 0, sy = 0, sxy = 0, sxx = 0
    pts.forEach((p) => {
      const v = p.r.kg as number
      sx += p.i; sy += v; sxy += p.i * v; sxx += p.i * p.i
    })
    const m = pts.length
    const b = (m * sxy - sx * sy) / (m * sxx - sx * sx || 1)
    const a = (sy - b * sx) / m
    const trend = { x1: x(0), y1: y(a), x2: x(n - 1), y2: y(a + b * (n - 1)) }

    const gridY = [0, 1, 2, 3].map((t) => {
      const v = lo + ((hi - lo) * t) / 3
      return { v, gy: y(v) }
    })

    // Libellés X éclaircis : 6 maxi, dernier toujours affiché
    const every = Math.ceil(n / 6)
    const xLabels = rows
      .map((r, i) => ({ r, i, x: x(i) }))
      .filter(({ i }) => {
        if (i === n - 1) return true
        if (i % every !== 0) return false
        return n - 1 - i >= every * 0.6
      })

    const line = catmullRom(pts)
    const last = pts[m - 1]
    const area = `${line} L${last.x},${PT + PLOT_H} L${pts[0].x},${PT + PLOT_H} Z`

    return { rows, n, W, pts, gridY, trend, xLabels, line, area }
  }, [points, windowN, avail])

  // « Tout » : position initiale sur la fin de l'historique
  useLayoutEffect(() => {
    if (windowN === 0 && scrollRef.current) {
      scrollRef.current.scrollLeft = geo.W
    }
  }, [windowN, geo.W])

  const handleMove = (clientX: number) => {
    const el = scrollRef.current?.querySelector("svg")
    if (!el) return
    const rect = el.getBoundingClientRect()
    const px = clientX - rect.left
    let best = 0
    geo.pts.forEach((p, i) => {
      if (Math.abs(p.x - px) < Math.abs(geo.pts[best].x - px)) best = i
    })
    setActive(best)
  }

  const activePt = active !== null && active < geo.pts.length ? geo.pts[active] : null
  const tipLeft = activePt ? Math.min(Math.max(activePt.x, 56), geo.W - 56) : 0

  const drawStyle: React.CSSProperties = reduce
    ? {}
    : { transition: "stroke-dashoffset 0.75s cubic-bezier(0.16,1,0.3,1)" }
  const areaStyle: React.CSSProperties = reduce
    ? {}
    : { transition: "opacity 0.5s ease 0.3s" }

  return (
    <div>
      {/* Sélecteur de fenêtre : pleine largeur, pastille glissante */}
      <div
        className="relative grid grid-cols-3 mb-3.5"
        style={{ background: "rgba(255,255,255,0.05)", borderRadius: 9, padding: 2 }}
      >
        <div
          className="absolute top-0.5"
          style={{
            height: "calc(100% - 4px)",
            width: "calc((100% - 4px) / 3)",
            left: 2,
            background: "rgba(6,182,212,0.18)",
            border: "0.5px solid rgba(34,211,238,0.45)",
            borderRadius: 7,
            transform: `translateX(${WINDOWS.findIndex((w) => w.n === windowN) * 100}%)`,
            transition: reduce ? undefined : "transform 0.26s cubic-bezier(0.16,1,0.3,1)",
          }}
        />
        {WINDOWS.map((w) => (
          <button
            key={w.n}
            onClick={() => { setWindowN(w.n); setActive(null) }}
            className="relative z-10 text-[11px] cursor-pointer"
            style={{
              padding: "5px 0",
              color: windowN === w.n ? C.cyan2 : "#e6edf5",
              fontWeight: windowN === w.n ? 500 : 400,
            }}
          >
            {w.label}
          </button>
        ))}
      </div>

      <div className="flex relative">
        {/* Axe Y dans un gutter séparé */}
        <svg width={AX_W} height={H} aria-hidden="true" style={{ flex: `0 0 ${AX_W}px` }}>
          {geo.gridY.map(({ v, gy }, i) => (
            <text key={i} x={AX_W} y={gy + 3.5} textAnchor="end" fontSize={10} fill={C.yLabel}>
              {Math.round(v)}
            </text>
          ))}
        </svg>

        <div
          ref={scrollRef}
          className={
            "flex-1 relative overflow-x-auto overflow-y-hidden pb-[6px] " +
            "[scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.16)_transparent] " +
            "[&::-webkit-scrollbar]:h-[4px] [&::-webkit-scrollbar-track]:bg-transparent " +
            "[&::-webkit-scrollbar-thumb]:bg-[rgba(255,255,255,0.16)] [&::-webkit-scrollbar-thumb]:rounded-[4px]"
          }
          style={{ marginLeft: GAP }}
        >
          <svg
            width={geo.W}
            height={H}
            role="img"
            aria-label={`Courbe du poids par semaine, de ${formatFr(geo.pts[0].r.kg as number)} kg à ${formatFr(geo.pts[geo.pts.length - 1].r.kg as number)} kg`}
            onMouseMove={(e) => handleMove(e.clientX)}
            onMouseLeave={() => setActive(null)}
            onTouchStart={(e) => handleMove(e.touches[0].clientX)}
            onTouchMove={(e) => handleMove(e.touches[0].clientX)}
          >
            <defs>
              <linearGradient id="wchart-area" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C.cyan} stopOpacity={0.26} />
                <stop offset="100%" stopColor={C.cyan} stopOpacity={0} />
              </linearGradient>
            </defs>

            {geo.gridY.map(({ gy }, i) => (
              <line key={i} x1={0} y1={gy} x2={geo.W} y2={gy} stroke="rgba(255,255,255,0.05)" strokeWidth={1} />
            ))}

            <path d={geo.area} fill="url(#wchart-area)" opacity={drawn ? 1 : 0} style={areaStyle} />

            <path
              d={`M${geo.trend.x1},${geo.trend.y1} L${geo.trend.x2},${geo.trend.y2}`}
              stroke="#94a3b8"
              strokeWidth={1.5}
              strokeDasharray="5 4"
              strokeLinecap="round"
              fill="none"
              opacity={0.65}
            />

            {activePt && (
              <line
                x1={activePt.x}
                y1={PT - 4}
                x2={activePt.x}
                y2={PT + PLOT_H}
                stroke="rgba(34,211,238,0.35)"
                strokeWidth={1}
              />
            )}

            <path
              d={geo.line}
              stroke={C.cyan}
              strokeWidth={2.5}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              pathLength={1}
              strokeDasharray={1}
              strokeDashoffset={drawn ? 0 : 1}
              style={drawStyle}
            />

            {/* Micro-points : affordance de survol, halo sur le dernier seulement */}
            {geo.pts.map((p, i) =>
              i === geo.pts.length - 1 ? null : (
                <circle key={i} cx={p.x} cy={p.y} r={2.4} fill={C.card} stroke={C.cyan2} strokeWidth={1.3} opacity={0.55} />
              )
            )}
            <circle cx={geo.pts[geo.pts.length - 1].x} cy={geo.pts[geo.pts.length - 1].y} r={7} fill={C.cyan} fillOpacity={0.22} />
            <circle cx={geo.pts[geo.pts.length - 1].x} cy={geo.pts[geo.pts.length - 1].y} r={3.6} fill={C.cyan2} stroke={C.card} strokeWidth={2} />

            {activePt && (
              <circle cx={activePt.x} cy={activePt.y} r={4.6} fill={C.cyan2} stroke={C.card} strokeWidth={2.5} />
            )}

            {geo.xLabels.map(({ r, i, x }) => (
              <text
                key={i}
                x={x}
                y={H - 8}
                textAnchor="middle"
                fontSize={10}
                fill={i === geo.n - 1 ? C.txt3 : C.yLabel}
              >
                S{r.w}
              </text>
            ))}
          </svg>

          {activePt && (
            <div
              className="absolute pointer-events-none"
              style={{
                left: tipLeft,
                top: Math.max(activePt.y - 52, 0),
                transform: "translateX(-50%)",
                background: "#131f33",
                border: "0.5px solid rgba(255,255,255,0.14)",
                borderRadius: 9,
                padding: "7px 10px",
                fontSize: 11,
                whiteSpace: "nowrap",
              }}
            >
              <span style={{ color: C.txt3 }}>
                S{geo.pts[active!].r.w} · {geo.pts[active!].r.label}
              </span>
              <br />
              <span style={{ fontWeight: 500, fontSize: 13, color: "#e6edf5" }}>
                {formatFr(geo.pts[active!].r.kg as number)} kg
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Légende */}
      <div
        className="flex items-center justify-center gap-[18px] mt-1"
        style={{ paddingTop: 10, borderTop: "0.5px solid rgba(255,255,255,0.06)" }}
      >
        <span className="flex items-center gap-1.5 text-[10px]" style={{ color: C.txt4 }}>
          <svg width={16} height={4}><line x1={0} y1={2} x2={16} y2={2} stroke={C.cyan} strokeWidth={2.5} strokeLinecap="round" /></svg>
          Poids relevé
        </span>
        <span className="flex items-center gap-1.5 text-[10px]" style={{ color: C.txt4 }}>
          <svg width={16} height={4}><line x1={0} y1={2} x2={16} y2={2} stroke="#94a3b8" strokeWidth={2} strokeDasharray="4 3" strokeLinecap="round" /></svg>
          Tendance
        </span>
      </div>
    </div>
  )
}
